import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { TableSkeleton } from '../../Components/common/Skeleton';
import { Share2 } from 'lucide-react';
import MarketingRegisterModal from '../../Components/marketingAgents/MarketingRegisterModal';
import Pagination from '../../Components/common/Pagination';

const MarketingAgentList = () => {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [filters, setFilters] = useState({
    verificationStatus: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    fetchAgents();
  }, [pagination.page, filters]);

  const fetchAgents = async () => {
    try {
      if (agents.length > 0) {
        setIsFetching(true);
      }
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.verificationStatus && { verificationStatus: filters.verificationStatus }),
        ...(filters.search && { search: filters.search })
      });

      const response = await api.get(`/marketing-agents?${params}`);
      setAgents(response.data.data.marketingAgents || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.data.pagination.total || 0,
        pages: response.data.data.pagination.pages || 0
      }));
    } catch (error) {
      toast.error('Failed to load Lead Partners');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const getVerificationColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      verified: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 w-32 bg-slate-200 animate-pulse rounded-lg"></div>
        </div>
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
          <TableSkeleton rows={8} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6" style={{ minHeight: '80vh' }}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h1 className="text-3xl font-black text-[#143E78] tracking-tight">Marketing Registry</h1>
          {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
            <button
              onClick={() => setShowRegisterModal(true)}
              className="flex items-center gap-2 bg-[#143E78] hover:bg-blue-800 text-white px-6 py-3 rounded-2xl font-black transition-all shadow-lg shadow-blue-900/10 active:scale-95 uppercase text-xs tracking-widest"
            >
              <Share2 size={18} />
              Register Lead Partner
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Search</label>
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search by name, region or ID"
                className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl outline-none font-bold transition-all text-sm"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Verification Status</label>
              <select
                name="verificationStatus"
                value={filters.verificationStatus}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl outline-none font-bold transition-all text-sm"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lead Partners Content */}
        <div 
          className={`bg-white rounded-lg shadow overflow-hidden transition-opacity duration-300 ${isFetching ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}
        >
          {/* Mobile View */}
          <div key={pagination.page} className="block lg:hidden divide-y divide-gray-100 animate-fadeIn">
            {agents.length > 0 ? (
              agents.map((agent) => {
                const firstName = agent.user?.firstName || '';
                const lastName = agent.user?.lastName || '';
                const displayName = firstName || lastName ? `${firstName} ${lastName}` : 'Unknown Agent';

                return (
                  <div key={agent._id} className="p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900">{displayName}</div>
                        <div className="text-xs text-gray-500">{agent.region || 'N/A'}</div>
                      </div>
                      <span className={`px-2 py-1 text-[10px] font-bold rounded-full ${getVerificationColor(agent.verificationStatus)}`}>
                        {agent.verificationStatus}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 gap-4 pt-2 border-t border-gray-50">
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase font-medium tracking-wider">Active Leads</p>
                        <p className="text-sm text-gray-900">{agent.activeLeads || 0}</p>
                      </div>
                    </div>

                    <Link
                      to={`/marketing-agents/${agent._id}`}
                      className="block w-full text-center py-2 bg-gray-50 text-blue-600 text-xs font-bold rounded-lg border border-gray-100"
                    >
                      View Profile
                    </Link>
                  </div>
                );
              })
            ) : (
              <div className="p-10 text-center text-gray-400 italic text-sm">
                No Lead Partners found
              </div>
            )}
          </div>

          {/* Desktop View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="sticky left-0 bg-gray-50 z-10 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px] shadow-[1px_0_0_0_#e5e7eb]">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                    Region
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                    ID Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                    CC (VERIFIED BY)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                    Active Leads
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                    Verification
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody key={pagination.page} className="bg-white divide-y divide-gray-200 animate-fadeIn">
                {agents.length > 0 ? (
                  agents.map((agent) => {
                    const firstName = agent.user?.firstName || '';
                    const lastName = agent.user?.lastName || '';
                    const displayName = `${firstName} ${lastName}`;

                    return (
                      <tr key={agent._id} className="hover:bg-gray-50 group">
                        <td className="sticky left-0 bg-white z-10 px-6 py-4 whitespace-nowrap group-hover:bg-gray-50 transition-colors shadow-[1px_0_0_0_#e5e7eb]">
                          <div className="text-sm font-medium text-gray-900">
                            {displayName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{agent.region || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{agent.identificationNumber || agent.user?.onboardingData?.panNumber || agent.user?.onboardingData?.aadhaarNumber || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-[11px] font-bold text-slate-900">{agent.user?.phone}</div>
                          <div className="text-[10px] text-slate-500 lowercase">{agent.user?.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center gap-2">
                              <span className="w-8 text-center text-[9px] font-black bg-slate-100 text-slate-500 px-1 py-0.5 rounded border border-slate-200">INV</span>
                              <span className="text-[10px] font-bold text-slate-700 truncate max-w-[120px]">
                                {agent.user?.invitedBy ? `${agent.user.invitedBy.firstName} ${agent.user.invitedBy.lastName}` : 'System'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="w-8 text-center text-[9px] font-black bg-indigo-100 text-indigo-500 px-1 py-0.5 rounded border border-indigo-200">VER</span>
                              <span className="text-[10px] font-bold text-indigo-700 truncate max-w-[120px]">
                                {agent.verifiedBy ? `${agent.verifiedBy.firstName} ${agent.verifiedBy.lastName}` : 'Pending'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-0.5 text-[10px] font-black rounded-lg bg-blue-50 text-blue-600 border border-blue-100">
                            {agent.activeLeads || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-bold rounded-full ${getVerificationColor(agent.verificationStatus)}`}>
                            {agent.verificationStatus}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <Link to={`/marketing-agents/${agent._id}`} className="inline-flex items-center px-3 py-1 bg-primary-50 text-primary-700 rounded-lg font-bold hover:bg-primary-100 transition-colors">
                            View
                          </Link>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500 italic">
                      No Lead Partners found matching the criteria
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest order-2 sm:order-1">
              Displaying {agents.length > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}-{(pagination.page - 1) * pagination.limit + agents.length} of {pagination.total} records
            </div>
            <div className="order-1 sm:order-2 w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.pages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        )}
      </div>

      <MarketingRegisterModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSuccess={() => fetchAgents()}
      />
    </>
  );
};

export default MarketingAgentList;
